import React from 'react';
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import styles from "./InvoiceDigestWidget.module.scss";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import { useStore, useStoreActions } from "../../../store/store-context";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import GenericError from "../../../components/GenericError/GenericError";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import { InvoicesDigestModel } from "../../../models/InvoicesDigestModel";
import InvoiceBar from "./InvoiceBar";
import colors from 'seagull/foundations/colors.module.scss';
import InvoicesInfoTable from "./InvoiceDigestTable";
export const makeNewInvoiceStatusGroup = (count, amount) => {
  return {
    count,
    amount: {
      amount
    }
  };
};
export function mockInvoiceDigestApi() {
  let {
    hasCreatedInvoice
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    hasCreatedInvoice: true
  };
  return {
    statuses: {
      requiresClientApproval: makeNewInvoiceStatusGroup(10, 1500000),
      paymentsVerificationRequired: makeNewInvoiceStatusGroup(10, 1500000),
      missingPaymentMethod: makeNewInvoiceStatusGroup(10, 1500000),
      failed: makeNewInvoiceStatusGroup(10, 1500000),
      disputed: makeNewInvoiceStatusGroup(10, 1500000),
      paymentStopped: makeNewInvoiceStatusGroup(0, 0)
    },
    processing: makeNewInvoiceStatusGroup(123, 12300),
    requiresAttention: makeNewInvoiceStatusGroup(123, 12300),
    hasCreatedInvoice: !!hasCreatedInvoice
  };
}
function MockInvoicesDigestBottomCard() {
  const invoiceDigest = InvoicesDigestModel.fromDTO(mockInvoiceDigestApi());
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.invoiceBarContainer
  }, /*#__PURE__*/React.createElement(InvoiceBar, {
    invoicesToDisplay: invoiceDigest.invoicesToDisplay(),
    totalAmount: invoiceDigest.totalAmount()
  })), /*#__PURE__*/React.createElement(InvoicesInfoTable, {
    invoiceDigestModel: invoiceDigest
  }));
}
const TopCard = _ref => {
  let {
    title,
    secondaryText,
    primaryText,
    titleInfo = '',
    loading,
    success,
    testid,
    linkTo
  } = _ref;
  return /*#__PURE__*/React.createElement(GraphWidgetTopCard, {
    className: styles.topCard,
    linkTo: linkTo
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": loading ? "".concat(testid, "-loading") : testid
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    showInfo: !!titleInfo,
    infoTestId: "".concat(testid, "-info"),
    info: titleInfo
  }, /*#__PURE__*/React.createElement(Typography, {
    ellipsis: true,
    variant: "paragraph1",
    skin: success ? 'success' : 'standard'
  }, title))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    weight: "normal",
    "data-testid": testid && "".concat(testid, "-amount")
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: 30
  }) : primaryText), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    className: styles.topCardBottomSection,
    variant: "paragraph2",
    skin: "secondary",
    "data-testid": "".concat(testid, "-count")
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: 20
  }) : "".concat(secondaryText, " invoices")))));
};
export const invoiceStatusToColor = {
  requiresClientApproval: '#6A9FF0',
  paymentsVerificationRequired: colors.purple300,
  missingPaymentMethod: '#A9E5F2',
  failed: '#FDA5EF',
  disputed: colors.red700,
  paymentStopped: '#e7049e'
};
const InvoicesDigestWidget = _ref2 => {
  let {
    invoicesDigest,
    isRefreshing,
    isFirstTimeLoading
  } = _ref2;
  const testIdPrefix = 'invoices-digest';
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    loading: isRefreshing,
    title: "Invoices",
    bottomHeight: "l"
  }, /*#__PURE__*/React.createElement(TopCard, {
    loading: isFirstTimeLoading,
    title: "Processing",
    success: true,
    testid: "".concat(testIdPrefix, "-processing"),
    titleInfo: /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testIdPrefix, "-processing-tooltip")
    }, "Funds still in transit from your clients\u2019 accounts. This process can take up to 3 business days and may be affected by refunds or payment failures."),
    secondaryText: invoicesDigest.processing().count,
    primaryText: /*#__PURE__*/React.createElement(Typography, {
      variant: "h5"
    }, invoicesDigest.processing().amount.display())
  }), /*#__PURE__*/React.createElement(TopCard, {
    loading: isFirstTimeLoading,
    title: "Require attention",
    testid: "".concat(testIdPrefix, "-require-attention"),
    secondaryText: invoicesDigest.requiresAttention().count,
    titleInfo: /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testIdPrefix, "-require-attention-tooltip")
    }, "The total value and number of invoices requiring your attention."),
    primaryText: /*#__PURE__*/React.createElement(Typography, {
      variant: "h5"
    }, invoicesDigest.requiresAttention().amount.display())
  }), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    "data-testid": "invoices-digest-bottom-card",
    loadingOrEmptyBg: () => {
      return !invoicesDigest.hasCreatedInvoice() || isFirstTimeLoading ? /*#__PURE__*/React.createElement(MockInvoicesDigestBottomCard, null) : null;
    },
    loading: isFirstTimeLoading,
    loadingMsg: "Loading data...",
    empty: invoicesDigest.invoicesTypeAmount() === 0,
    emptyMsg: invoicesDigest.hasCreatedInvoice() ? /*#__PURE__*/React.createElement("div", {
      "data-testid": "invoice-ever-created-covering-text"
    }, "No invoice requires your attention") : /*#__PURE__*/React.createElement("div", {
      "data-testid": "never-created-invoice-covering-text"
    }, "Here you will view invoices that require your attention")
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.invoiceBarContainer
  }, /*#__PURE__*/React.createElement(InvoiceBar, {
    invoicesToDisplay: invoicesDigest.invoicesToDisplay(),
    totalAmount: invoicesDigest.totalAmount()
  })), /*#__PURE__*/React.createElement(InvoicesInfoTable, {
    invoiceDigestModel: invoicesDigest
  })));
};
export default function InvoicesDigestWidgetFetch() {
  const {
    fetchInvoicesDigest
  } = useStoreActions();
  const initialData = useStore(state => state.invoicesDigest.data);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetchInvoicesDigest,
    initialData: initialData,
    renderChild: _ref3 => {
      let {
        data,
        loading: isRefreshing
      } = _ref3;
      return /*#__PURE__*/React.createElement(InvoicesDigestWidget, {
        invoicesDigest: data,
        isRefreshing: isRefreshing,
        isFirstTimeLoading: false
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(InvoicesDigestWidget, {
      invoicesDigest: InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()),
      isRefreshing: true,
      isFirstTimeLoading: true
    }),
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "widget-error"
    })
  });
}